import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  addNavigationApi,
  disableUserApi,
  fetchAllNavigation,
  fetchAllUsers,
  fetchRoleNavigationByRole,
  fetchSalesRepsApi,
  fetchUserNamesApi,
  getDisabledUsersApi,
  handleRegister,
  updateRoleNavigationApi,
} from "../../api/user.api";
import { IUser, Navigations, NavItem } from "../../models/userData.model";
import { PaginationRes } from "../../models/response.model";

interface User {
  userBasedOnRoles: any[];
  _id: string;
  username: string;
  email: string;
  password: string;
  mobileNumber: string;
  states: string[];
  role: string;
  manager?: string;
  warehouseAssigned?: string;
}

interface UserState {
  user: User[];
  disabledUsers: User[];
  userBasedOnRoles: any[];
  loading: boolean;
  allUsersNames: { _id: string; username: string }[];
  salesReps: { _id: string; name: string; mobileNumber: string }[];
  salesRepsPagination?: PaginationRes;
  cseUsers: IUser[];
  roleNavigation: Navigations | null;
  allNavigation: NavItem[];
}

const initialState: UserState = {
  userBasedOnRoles: [],
  user: [],
  loading: false,
  allUsersNames: [],
  salesReps: [],
  cseUsers: [],
  roleNavigation: null,
  disabledUsers: [],
  allNavigation: [],
};

// Fetch users
export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchAllUsers();
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch users");
    }
  },
);

// Add user
export const handleAddUsers = createAsyncThunk(
  "users/handleAddUser",
  async (newUser: any, { rejectWithValue }) => {
    try {
      return await handleRegister(newUser);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to add user");
    }
  },
);

export const fetchUserNames = createAsyncThunk(
  "users/fetchNames",
  async (search: string | undefined, { rejectWithValue }) => {
    try {
      return await fetchUserNamesApi(search);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch user names");
    }
  },
);

export const fetchSalesReps = createAsyncThunk(
  "users/fetchSalesReps",
  async (
    payload: {
      userId: string;
      params: { search?: string; page: number; limit: number };
    },
    { rejectWithValue },
  ) => {
    try {
      return await fetchSalesRepsApi(payload.userId, payload.params);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch sales reps");
    }
  },
);

export const getRoleNavigationByRole = createAsyncThunk(
  "users/fetchRoleNavigation",
  async (role: string, { rejectWithValue }) => {
    try {
      return await fetchRoleNavigationByRole(role);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch navigation");
    }
  },
);

export const addNavigation = createAsyncThunk(
  "navigation/addNavigation",
  async (navigations: NavItem, { rejectWithValue }) => {
    try {
      return await addNavigationApi(navigations);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to add navigation");
    }
  },
);

export const getAllNavigation = createAsyncThunk(
  "navigation/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchAllNavigation();
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  },
);

export const updateRoleNavigation = createAsyncThunk(
  "users/updateRoleNavigation",
  async (data: any, { rejectWithValue }) => {
    try {
      return await updateRoleNavigationApi(data);
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  },
);

export const disableUser = createAsyncThunk(
  "users/disableUser",
  async (userId: string, { rejectWithValue }) => {
    try {
      return await disableUserApi(userId);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to disable user");
    }
  },
);

export const getDisabledUsers = createAsyncThunk(
  "users/getDisabledUsers",
  async (_, { rejectWithValue }) => {
    try {
      return await getDisabledUsersApi();
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch disabled users");
    }
  },
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch Users
    builder.addCase(fetchUsers.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchUsers.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload;
    });
    builder.addCase(fetchUsers.rejected, (state) => {
      state.loading = false;
    });

    // Add User
    builder.addCase(handleAddUsers.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      handleAddUsers.fulfilled,
      (state, action: PayloadAction<User>) => {
        state.loading = false;
        state?.user?.push(action.payload);
      },
    );
    builder
      .addCase(handleAddUsers.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchUserNames.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserNames.fulfilled, (state, action) => {
        state.loading = false;
        state.allUsersNames = action.payload;
      })
      .addCase(fetchUserNames.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchSalesReps.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSalesReps.fulfilled, (state, action) => {
        state.loading = false;
        state.salesReps = action.payload.data;
        state.salesRepsPagination = action.payload.pagination;
      })
      .addCase(fetchSalesReps.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getRoleNavigationByRole.pending, (state) => {
        state.loading = true;
      })
      .addCase(getRoleNavigationByRole.fulfilled, (state, action) => {
        state.loading = false;
        state.roleNavigation = action.payload;
      })
      .addCase(getRoleNavigationByRole.rejected, (state) => {
        state.loading = false;
      })
      .addCase(addNavigation.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addNavigation.pending, (state) => {
        state.loading = true;
      })
      .addCase(addNavigation.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getAllNavigation.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllNavigation.fulfilled, (state, action) => {
        state.loading = false;
        state.allNavigation = action.payload?.navigations || [];
      })
      .addCase(getAllNavigation.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateRoleNavigation.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateRoleNavigation.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateRoleNavigation.rejected, (state) => {
        state.loading = false;
      })
      .addCase(disableUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(disableUser.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(disableUser.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getDisabledUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDisabledUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.disabledUsers = action.payload;
      })
      .addCase(getDisabledUsers.rejected, (state) => {
        state.loading = false;
      });
  },
});

export default userSlice.reducer;
